import React, { useState } from 'react';

// Shared weekly schedule grid (168 hourly slots, Mon–Sun) used by both the
// vitrine monitor and the MAP repricer's own standalone check cycle.
// All 24 hours stay available; the "без ночи" switch is an opt-in that keeps the
// quick presets out of 00–07, where competitor prices barely move.
export const DAYS = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'];
export const REGIONS = [
  'Москва', 'Санкт-Петербург', 'Новосибирск', 'Екатеринбург',
  'Нижний Новгород', 'Казань', 'Самара', 'Омск', 'Челябинск',
  'Ростов-на-Дону', 'Уфа', 'Волгоград', 'Пермь', 'Красноярск',
  'Воронеж', 'Саратов', 'Тюмень', 'Тольятти', 'Ижевск', 'Барнаул',
];

export const NIGHT_END = 8; // hours 00..07 are the "night" block
const ALL_HOURS = Array.from({ length: 24 }, (_, h) => h);

export function emptySchedule() { return new Array(168).fill(false); }

function hasNight(schedule) {
  for (let h = 0; h < NIGHT_END; h++) for (let d = 0; d < 7; d++) if (schedule[h * 7 + d]) return true;
  return false;
}

// Presets describe the pattern over (hour, dayIndex); `hours` is whatever range
// the "без ночи" switch leaves open.
const PRESETS = [
  { label: 'Шахматка', title: 'Через час, со сдвигом на каждый день', pred: (h, d) => (h + d) % 2 === 0 },
  { label: 'Обратная', title: 'Шахматка наоборот', pred: (h, d) => (h + d) % 2 === 1 },
  ...[3, 4, 5, 6].map(step => ({
    label: `Раз в ${step} ч`,
    title: `Каждые ${step} часа, начиная с первого доступного`,
    pred: (h, d, hours) => (h - hours[0]) % step === 0,
  })),
];

export function ScheduleGrid({ value, onChange }) {
  const schedule = value && value.length === 168 ? value : emptySchedule();
  // Opt-in by default, unless the saved schedule already uses night hours.
  const [skipNight, setSkipNight] = useState(() => !hasNight(schedule));
  // Last pattern applied from a button, so flipping "без ночи" can rebuild it
  // over the new window instead of just cutting slots out of it.
  const [lastPred, setLastPred] = useState(null);
  const hours = skipNight ? ALL_HOURS.slice(NIGHT_END) : ALL_HOURS;

  const fill = (pred, hoursOverride) => {
    const hrs = hoursOverride || hours;
    const n = emptySchedule();
    for (const h of hrs) for (let d = 0; d < 7; d++) n[h * 7 + d] = !!pred(h, d, hrs);
    setLastPred(() => pred);
    onChange(n);
  };
  const toggle = (idx) => {
    const n = [...schedule]; n[idx] = !n[idx];
    setLastPred(null);
    onChange(n);
  };
  const toggleHour = (h) => {
    const n = [...schedule], base = h * 7;
    const anyOff = DAYS.some((_, d) => !n[base + d]);
    for (let d = 0; d < 7; d++) n[base + d] = anyOff;
    setLastPred(null);
    onChange(n);
  };
  const toggleDay = (day) => {
    const n = [...schedule];
    const anyOff = hours.some(h => !n[h * 7 + day]);
    for (const h of hours) n[h * 7 + day] = anyOff;
    setLastPred(null);
    onChange(n);
  };
  const toggleSkipNight = (next) => {
    setSkipNight(next);
    const nextHours = next ? ALL_HOURS.slice(NIGHT_END) : ALL_HOURS;
    if (lastPred) { fill(lastPred, nextHours); return; }
    if (next && hasNight(schedule)) {
      const n = [...schedule];
      for (let h = 0; h < NIGHT_END; h++) for (let d = 0; d < 7; d++) n[h * 7 + d] = false;
      onChange(n);
    }
  };

  const activeCount = schedule.filter(Boolean).length;
  return (
    <div className="vm-sched">
      <div className="vm-sched__controls">
        {PRESETS.map(p => (
          <button
            key={p.label}
            className="btn btn--ghost btn--xs"
            title={p.title}
            onClick={() => fill(p.pred)}
          >
            {p.label}
          </button>
        ))}
        <button className="btn btn--ghost btn--xs" onClick={() => onChange(emptySchedule())}>Очистить</button>
        <button className="btn btn--ghost btn--xs" onClick={() => fill(() => true)}>Все часы</button>
        <label className="vm-sched__night" title="Пресеты не будут заполнять 00–07">
          <input type="checkbox" checked={skipNight} onChange={e => toggleSkipNight(e.target.checked)} />
          без ночи (00–07)
        </label>
        <span className="vm-sched__hint">
          {activeCount} слотов активно
          {activeCount > 0 && ` · ~${(activeCount / 7).toFixed(1)} проходов в день`}
        </span>
      </div>
      <div className="vm-sched__grid">
        <div className="vm-sched__corner" />
        {DAYS.map((d, i) => (
          <div key={d} className="vm-sched__day-hdr" onClick={() => toggleDay(i)}>{d}</div>
        ))}
        {ALL_HOURS.map(h => (
          <React.Fragment key={h}>
            <div
              className={`vm-sched__hour-lbl${skipNight && h < NIGHT_END ? ' vm-sched__hour-lbl--night' : ''}`}
              onClick={() => toggleHour(h)}
            >
              {String(h).padStart(2, '0')}
            </div>
            {DAYS.map((_, d) => {
              const idx = h * 7 + d;
              return (
                <div
                  key={idx}
                  className={`vm-sched__cell${schedule[idx] ? ' vm-sched__cell--on' : ''}`}
                  onClick={() => toggle(idx)}
                />
              );
            })}
          </React.Fragment>
        ))}
      </div>
    </div>
  );
}
